import * as O from 'fp-ts/lib/Option';
import {Paper, Table as MaterialTable, TableBody as MaterialTableBody, TableFooter} from '@material-ui/core';
import {isLoaded, matchAsyncPage} from 'Core/models/Async';
import * as A from 'fp-ts/lib/Array';
import {pipe} from 'fp-ts/lib/pipeable';
import * as I from 'immutable';
import React, {useEffect, useState} from 'react';
import Loading from 'Shared/components/Loading';
import {SharedPagedProps} from 'Shared/components/PagedView/PagedView';
import 'Shared/components/PagedView/Table/Table.sass';
import TableBody from 'Shared/components/PagedView/Table/TableBody';
import TableHeader from 'Shared/components/PagedView/Table/TableHeader';
import VisibleColumnsDrawer from 'Shared/components/PagedView/Table/VisibleColumnsDrawer';
import {LocalStorageKey, fromLocalStorage} from 'Shared/utilities/localStorage';
import {safeParseJson} from 'Shared/utilities/parsers';
import {flow} from 'fp-ts/lib/function';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';

export type ReactWindowProps<TData> = {
  index: number;
  dataItem: TData;
};

export type TableProps<TData> = SharedPagedProps<TData>;
export const ColumnWidthsContext = React.createContext(I.Map<string, number>());

export const ROW_SIZE = 48;
const NoResultsMessage = <div className="paged-table__content">No Results Available.</div>;
const isScrollbarShowing = (height: number, rowCount: number) => height >= rowCount * ROW_SIZE;

type SavedVisibilityPreferencesDictionary = {
  [userId: string]: {
    [pagedViewKey: string]: I.Map<string, boolean>;
  };
};

export const getSavedColumnVisibilityPreferences = flow(
  fromLocalStorage(LocalStorageKey.PagedViewVisibilityPreferences),
  O.fromNullable,
  O.chain(safeParseJson<SavedVisibilityPreferencesDictionary>())
);

const getSavedColumnVisibilityPreferencesForUserByPagedView = (userId: string, pagedViewKey: string) => {
  const savedColumnPreferencesOption = getSavedColumnVisibilityPreferences();

  return pipe(
    savedColumnPreferencesOption,
    O.map(savedColumnPreferences => savedColumnPreferences[userId]),
    O.chain(O.fromNullable),
    O.map(savedColumnPreferencesForUser => savedColumnPreferencesForUser[pagedViewKey]),
    O.chain(O.fromNullable),
    O.map(preferences => I.Map<string, boolean>(preferences))
  );
};

const Table = <TData extends {}>(props: TableProps<TData>) => {
  const {pagedViewKey, additionalFilterProperties, columns, actions, data, sort, onChangeSort} = props;
  const {userClaims} = useSelector((state: ReducerState) => state.user.profile);

  const defaultVisibleColumnsWithoutPreferences: I.Map<string, boolean> = pipe(
    columns.filter(col => col.allowHide).map(col => ({[col.title]: !col.hiddenByDefault})),
    A.reduce({}, (acc, col) => ({...acc, ...col})),
    I.OrderedMap
  );

  const defaultVisibleColumns = isLoaded(userClaims)
    ? pipe(
        getSavedColumnVisibilityPreferencesForUserByPagedView(userClaims.Data.id, pagedViewKey),
        O.getOrElse(() => defaultVisibleColumnsWithoutPreferences)
      )
    : defaultVisibleColumnsWithoutPreferences;

  const defaultColumnWidths: I.Map<string, number> = pipe(
    columns.map(column => ({[column.title]: (1 / columns.length) * 100})),
    A.reduce({}, (acc, col) => ({...acc, ...col})),
    I.OrderedMap
  );

  const [columnWidths, setColumnWidths] = useState(defaultColumnWidths);
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  const columnsToShow = columns.filter(col => visibleColumns.get(col.title) || !col.allowHide);

  const [height, setHeight] = useState(0);
  const [totalWidth, setTotalWidth] = useState(0);
  const [isColumnDrawerOpen, setIsColumnDrawerOpen] = useState(false);

  useEffect(() => {
    const newWidthPerColumn = (1 / columnsToShow.length) * 100;
    const newColumnWidths: I.Map<string, number> = pipe(
      columns.map(column => ({[column.title]: newWidthPerColumn})),
      A.reduce({}, (acc, col) => ({...acc, ...col})),
      I.OrderedMap
    );
    setColumnWidths(newColumnWidths);
  }, [totalWidth, columns, columnsToShow.length]);

  return (
    <ColumnWidthsContext.Provider value={columnWidths}>
      <Paper className="paged-table__paper" square>
        <VisibleColumnsDrawer
          columnsMap={visibleColumns}
          onClose={() => setIsColumnDrawerOpen(false)}
          open={isColumnDrawerOpen}
          onColumnVisibilityChange={(key, isVisible) => setVisibleColumns(visibleColumns.set(key, isVisible))}
          pagedViewKey={pagedViewKey}
        />
        <MaterialTable component="div" className="paged-table">
          <div className="paged-table__head">
            <TableHeader
              onChangeSort={onChangeSort}
              sort={sort}
              actions={actions}
              additionalFilterProperties={additionalFilterProperties}
              isScrollbarShowing={isLoaded(data) && isScrollbarShowing(height, data.Data.totalCount)}
              onOpenColumnDrawer={() => setIsColumnDrawerOpen(true)}
              columnsForHeaders={columnsToShow}
              allColumns={columns}
              onColumnResize={(columnTitle, newWidthDelta) => {
                const currentWidth = columnWidths.get(columnTitle);
                const titleOfNextColumn: string = columnWidths
                  .skipUntil((width, title) => title === columnTitle)
                  .skip(1)
                  .keys()
                  .next().value;
                const nextColumnCurrentWidth = columnWidths.get(titleOfNextColumn);
                const toCurrentPixelsOfTotal = (percentWidth: number) => totalWidth / (100 / percentWidth);

                if (currentWidth && nextColumnCurrentWidth) {
                  const newWidths = columnWidths
                    .set(columnTitle, ((toCurrentPixelsOfTotal(currentWidth) + newWidthDelta) / totalWidth) * 100)
                    .set(
                      titleOfNextColumn,
                      ((toCurrentPixelsOfTotal(nextColumnCurrentWidth) - newWidthDelta) / totalWidth) * 100
                    );
                  setColumnWidths(newWidths);
                }
              }}
            />
          </div>
          <MaterialTableBody className="paged-table__body" component="div">
            {matchAsyncPage(props.data, {
              Conflict: () => <div className="paged-table__content">An error has occurred.</div>,
              Loaded: page =>
                page.totalCount === 0 ? (
                  NoResultsMessage
                ) : (
                  <TableBody
                    setWidth={setTotalWidth}
                    setHeight={setHeight}
                    loadMore={props.loadMore}
                    data={page}
                    columns={columnsToShow}
                  />
                ),
              ServerError: () => <div className="paged-table__content">An error has occurred.</div>,
              Unauthorized: () => (
                <div className="paged-table__content">You are not authorized to access this resource.</div>
              ),
              Loading: () => (
                <div className="paged-table__content">
                  <Loading />
                </div>
              ),
            })}
          </MaterialTableBody>
        </MaterialTable>
        <TableFooter className="paged-table__footer" component="div">
          {props.data.key === 'LoadingAdditional' || props.data.key === 'Loaded' ? (
            <span id={`table-footer-results-${pagedViewKey}`}>Total Results: {props.data.Data.totalCount}</span>
          ) : null}
        </TableFooter>
      </Paper>
    </ColumnWidthsContext.Provider>
  );
};

export default Table;
